import type { PointerEvent } from "react";

/**
 * Pointer-driven holo interaction, ported from simeydotme/pokemon-cards-css
 * (https://github.com/simeydotme/pokemon-cards-css — MIT-style demo source).
 *
 * Sets CSS custom properties on the card element:
 *   --px/--py   pointer position within the card (%)
 *   --rx/--ry   card tilt (deg) — applied as rotateY(--rx) rotateX(--ry)
 *   --bgx/--bgy shine gradient offset (37..63% / 33..67%)
 *   --pfc       pointer distance from center (0..1)
 *   --pfl/--pft pointer from left/top (0..1)
 */
const clamp = (value: number, min = 0, max = 100) => Math.min(Math.max(value, min), max);
const adjust = (value: number, fromMin: number, fromMax: number, toMin: number, toMax: number) =>
  toMin + ((toMax - toMin) * (value - fromMin)) / (fromMax - fromMin);

const frames = new WeakMap<HTMLElement, number>();

export function holoMove(event: PointerEvent<HTMLElement>) {
  const el = event.currentTarget;
  const { clientX, clientY, pointerType } = event;
  if (pointerType !== "mouse" && pointerType !== "pen") return;
  const pending = frames.get(el);
  if (pending) cancelAnimationFrame(pending);
  frames.set(el, requestAnimationFrame(() => {
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const px = clamp((100 / rect.width) * (clientX - rect.left));
    const py = clamp((100 / rect.height) * (clientY - rect.top));
    const cx = px - 50;
    const cy = py - 50;
    el.classList.add("interacting");
    el.style.setProperty("--px", `${px.toFixed(2)}%`);
    el.style.setProperty("--py", `${py.toFixed(2)}%`);
    el.style.setProperty("--rx", `${(-(cx / 3.5)).toFixed(2)}deg`);
    el.style.setProperty("--ry", `${(cy / 3.5).toFixed(2)}deg`);
    el.style.setProperty("--bgx", `${adjust(px, 0, 100, 37, 63).toFixed(2)}%`);
    el.style.setProperty("--bgy", `${adjust(py, 0, 100, 33, 67).toFixed(2)}%`);
    el.style.setProperty("--pfc", `${clamp(Math.sqrt(cy * cy + cx * cx) / 50, 0, 1).toFixed(3)}`);
    el.style.setProperty("--pfl", `${(px / 100).toFixed(3)}`);
    el.style.setProperty("--pft", `${(py / 100).toFixed(3)}`);
  }));
}

export function holoLeave(event: PointerEvent<HTMLElement>) {
  const el = event.currentTarget;
  const pending = frames.get(el);
  if (pending) cancelAnimationFrame(pending);
  el.classList.remove("interacting");
  for (const prop of ["--px", "--py", "--rx", "--ry", "--bgx", "--bgy", "--pfc", "--pfl", "--pft"]) {
    el.style.removeProperty(prop);
  }
}
